import { LoadingButton } from "@mui/lab";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import parsePhoneNumberFromString from "libphonenumber-js";
import { FC, useCallback, useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useMutation } from "react-query";
import { AddressForm } from "../../components/AddressForm";
import { useConfig } from "../../utils/useConfig";
import { useDynamicEmployerValidationSchema } from "../../utils/useDynamicValidationSchema";
import { EmployerCompanySize, EmployerSource } from "../../models";

export type OnboardingFormValuesType = {
  contactFirstName: string;
  contactLastName: string;
  name: string;
  companySize: string;
  contact: {
    phone: string;
    email: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      stateOrProvince: string;
      postalCode: string;
      country: any;
    };
  };
  renewalOptions: {
    startDate: string;
  };
};

export const OnboardingForm: FC<{ setSignupComplete: () => void }> = ({
  setSignupComplete
}) => {
  const { config } = useConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isAcceptedTCs, setIsAcceptedTCs] = useState(false);
  const [country, setCountry] = useState<any>("NZ");

  const companySizeOptions = [
    { label: "1-350 Employees", value: EmployerCompanySize.SMALL },
    { label: "351-1000 Employees", value: EmployerCompanySize.MEDIUM },
    { label: "1000+ Employees", value: EmployerCompanySize.LARGE }
  ];

  const validationSchema = useDynamicEmployerValidationSchema(country);
  const [error, setError] = useState("");

  const createEmployerMutation = useMutation(
    async ({
      formValues,
      recaptchaToken
    }: {
      formValues: any;
      recaptchaToken: string | undefined;
    }) => {
      if (!isAcceptedTCs)
        throw new Error("Please accept our Terms and Conditions to proceed.");
      if (!recaptchaToken) throw new Error("ReCaptcha Error");

      const formattedPhoneNumber = parsePhoneNumberFromString(
        formValues.contact.phone,
        country
      )?.number;

      const response = await fetch(`${config?.API_URL}/employers`, {
        method: "POST",
        headers: {
          "recaptcha-token": recaptchaToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formValues,
          contact: {
            ...formValues.contact,
            phone: formattedPhoneNumber,
            address: {
              ...formValues.contact.address,
              stateOrProvince:
                formValues.contact.address.country !== "NZ"
                  ? formValues.contact.address.stateOrProvince
                  : undefined
            }
          },
          source: EmployerSource.SELF_SERVICE
        })
      });

      if (!response.ok) {
        let responseJson;
        try {
          responseJson = await response.json();
        } catch (err) {
          throw new Error("There was a problem signing up.");
        }

        switch (response.status) {
          case 409:
            throw new Error("Email already in use. Please contact support");
          case 400:
            if (responseJson.errors["Contact.Phone"]) {
              throw new Error(responseJson.errors["Contact.Phone"][0]);
            } else if (responseJson.errors["Contact.Email"]) {
              throw new Error(responseJson.errors["Contact.Email"][0]);
            } else {
              throw new Error("There was a problem signing up.");
            }
          default:
            throw new Error("There was a problem signing up.");
        }
      }
    },
    {
      onSuccess: () => {
        setSignupComplete();
      },
      onError: (error: Error) => {
        console.error(error.message);
        setError(error.message);
      }
    }
  );

  const { resetForm, ...formik } = useFormik({
    initialValues: {
      contactFirstName: "",
      contactLastName: "",
      name: "",
      companySize: "",
      contact: {
        phone: "",
        email: "",
        address: {
          address1: "",
          address2: "",
          city: "",
          stateOrProvince: "",
          postalCode: "",
          country
        }
      },
      renewalOptions: {
        startDate: new Date().toISOString()
      }
    },
    validationSchema,
    onSubmit: async (values) => {
      const recaptchaToken = await handleReCaptchaVerify();
      createEmployerMutation.mutate({ formValues: values, recaptchaToken });
    }
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }

    return await executeRecaptcha("onboarding");
  }, [executeRecaptcha]);

  useEffect(() => {
    setCountry(formik.values.contact.address.country);
  }, [formik.values.contact.address.country]);

  useEffect(() => {
    if (country === "NZ") {
      formik.setFieldValue("contact.address.stateOrProvince", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              value={formik.values.name}
              onChange={formik.handleChange}
              label="Company Name"
              name="name"
              required
              fullWidth
              size="small"
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={
                formik.touched.name &&
                formik.errors.name?.replace("name", "Company Name")
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              displayEmpty
              value={formik.values.companySize}
              onChange={formik.handleChange}
              name="companySize"
              size="small"
              required
              fullWidth
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <Typography color="grey">Company Size*</Typography>;
                }

                return companySizeOptions.find((cso) => cso.value === selected)
                  ?.label;
              }}
              error={
                formik.touched.companySize && Boolean(formik.errors.companySize)
              }
            >
              {companySizeOptions.map((cso) => {
                return <MenuItem value={cso.value}>{cso.label}</MenuItem>;
              })}
            </Select>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={3} mb={3}>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contactFirstName}
              onChange={formik.handleChange}
              label="Contact First Name"
              name="contactFirstName"
              required
              fullWidth
              size="small"
              error={
                formik.touched.contactFirstName &&
                Boolean(formik.errors.contactFirstName)
              }
              helperText={
                formik.touched.contactFirstName &&
                formik.errors.contactFirstName?.replace(
                  "contactFirstName",
                  "Contact First Name"
                )
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contactLastName}
              onChange={formik.handleChange}
              label="Contact Last Name"
              name="contactLastName"
              required
              fullWidth
              size="small"
              error={
                formik.touched.contactLastName &&
                Boolean(formik.errors.contactLastName)
              }
              helperText={
                formik.touched.contactLastName &&
                formik.errors.contactLastName?.replace(
                  "contactLastName",
                  "Contact Last Name"
                )
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contact.email}
              onChange={formik.handleChange}
              label="Contact Email"
              name="contact.email"
              required
              fullWidth
              size="small"
              error={
                formik.touched.contact?.email &&
                Boolean(formik.errors.contact?.email)
              }
              helperText={
                formik.touched.contact?.email &&
                formik.errors.contact?.email?.replace(
                  "contact.email",
                  "Contact Email"
                )
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={formik.values.contact.phone}
              onChange={formik.handleChange}
              label="Contact Phone"
              name="contact.phone"
              required
              fullWidth
              size="small"
              type="tel"
              error={
                formik.touched.contact?.phone &&
                Boolean(formik.errors.contact?.phone)
              }
              helperText={
                formik.touched.contact?.phone &&
                formik.errors.contact?.phone?.replace(
                  "contact.phone",
                  "Contact Phone"
                )
              }
            />
          </Grid>
        </Grid>

        {config?.GOOGLE_PLACES_API_KEY && (
          <AddressForm formik={{ resetForm, ...formik }} />
        )}

        <Grid container spacing={2} my={1}>
          {error && (
            <Grid item xs={12}>
              <Typography color="error">{error}</Typography>
            </Grid>
          )}
        </Grid>

        <FormControlLabel
          control={
            <Checkbox
              required
              onChange={(e) => {
                setIsAcceptedTCs(e.target.checked);
              }}
            />
          }
          label={
            <span>
              I accept the{" "}
              <Link href="https://example.com/terms" target="_blank">
                terms and conditions
              </Link>
            </span>
          }
        />

        <LoadingButton
          sx={{
            marginLeft: "auto"
          }}
          type="submit"
          variant="contained"
          color="primary"
          loading={
            createEmployerMutation.isLoading || createEmployerMutation.isLoading
          }
        >
          Sign Up
        </LoadingButton>
      </Grid>
    </form>
  );
};
